import { useResponseHeader, useRuntimeConfig } from '#imports'
import { type MaybeRefOrGetter, toValue, watch } from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { isTestEnv } from '@backmarket/utils/env/isTestEnv'

/**
 * All these composables are used to add tags to the 'Cache-Tag' header.
 * This header is used to purge the Cloudflare cache of various HTML pages (e.g. Product, Product Listing or CMS pages)
 *
 * The 'Cache-Tag' header is a comma-separated list of tags that are used to identify the pages that need to be purged from the cache.
 *
 * The tags are added to the header in the following order:
 * 1. Service-specific tag (e.g. front-office-prod-eu)
 * 2. Product-specific tags (e.g. prod-eu-a, prod-eu-b, prod-eu-c)
 * 3. CMS-specific tags (e.g. prod-eu-cms-fr-FR-article-test) @see @link front-office/scopes/cms/composables/useFetchPageContent.ts for this
 *
 * See this doc for more details: https://backmarket.atlassian.net/wiki/spaces/FRONT/pages/5082054712/Invalidation-based+Caching+Strategy
 *
 */

/**
 * This composable adds one or more tags to the 'Cache-Tag' header.
 * It appends and deduplicates tags
 * The 'Cache-Tag' header is used to purge the Cloudflare cache of various HTML pages (e.g. Product, Product Listing or CMS pages) 

 * @returns void
 */
export function useAddCacheTagHeader(...newTags: string[]) {
  // we only run the code on the server or if it's a test
  // when running tests with @nuxt/test-utils, import.meta.client is true
  if (import.meta.client && !isTestEnv()) return

  const addCacheTagHeaderFn = useAddCacheTagHeaderFn()

  addCacheTagHeaderFn(...newTags)
}

function useAddCacheTagHeaderFn() {
  const logger = useLogger()

  const header = useResponseHeader('Cache-Tag')

  return (...newTags: string[]) => {
    try {
      const currentHeaderValue = (header.value || '') as string
      // avoid creating array [''] otherwise the empty string will be included in the
      // final Cache-Tag header (e.g. ",tag1,tag2")
      const currentTags = currentHeaderValue
        ? currentHeaderValue.split(',')
        : []

      const uniqueTags = [...new Set([...currentTags, ...newTags])]
      const updatedValue = uniqueTags.join(',')

      if (updatedValue) header.value = updatedValue
    } catch (error) {
      logger.error('[Cache-Tag] Error setting Cache-Tag header', {
        owners: ['bot-chapter-frontend-core-pastrami'],
        error: error as Error,
      })
    }
  }
}

/**
 * Shared SSR-safe primitive: watches a reactive source, maps it to Cache-Tag
 * tags and appends them to the response header.
 *
 * flush: "sync" allows the callback to run as soon as the source updates (e.g.
 * once a `useAsyncData` resolves), before the response is sent. With the default
 * "pre" flush the callback is queued by Vue's scheduler and may not run on SSR.
 *
 * `useAddCacheTagHeaderFn` is resolved once here, in setup context, so the inner
 * callback never calls Nuxt composables after an `await` boundary.
 */
function useWatchAndAddCacheTags<T>(
  source: () => T,
  toTags: (value: T) => string[] | undefined,
) {
  const addCacheTagHeaderFn = useAddCacheTagHeaderFn()

  watch(
    source,
    (value) => {
      const tags = toTags(value)

      if (tags?.length) addCacheTagHeaderFn(...tags)
    },
    // flush: "sync" allows to run the callback function as soon "header" updates, before the response is sent
    // in the default behaviour ("pre"), the callback is queued by Vue's scheduler and may not run on SSR
    { flush: 'sync', immediate: true },
  )
}

/**
 * useAddServiceCacheTagHeader adds a service-specific tag to the Cache-Tag header.
 * This allows to purge the Cloudflare cache of all pages served by a specific service (e.g. front-office-prod-eu)
 */
export function useAddServiceCacheTagHeader() {
  const logger = useLogger()
  try {
    // example: front-office-prod-eu
    const { service, environment } = useRuntimeConfig().public.httpcache
    const serviceTag = `${service}-${environment}`

    useAddCacheTagHeader(serviceTag)
  } catch (error) {
    logger.error('[Cache-Tag] Error setting Cache-Tag header', {
      owners: ['bot-chapter-frontend-core-pastrami'],
      error: error as Error,
    })
  }
}

/**
 * This composable is used to add a product Cache-Tag header to the response.
 * It is used to purge the Cloudflare cache of HTML pages with contained products (e.g. Product, Product Listing or CMS pages)
 *
 * Example:
 * On front-office prod-eu, on a Product Listing Page with products of ids a, b and c, it sets the Cache-Tag header
 * "front-office-prod-eu,prod-eu-a,prod-eu-b,prod-eu-c"
 *
 * @param products - The list of products to add to the Cache-Tag header. (e.g. [{ id: 'a' }, { id: 'b' }, { id: 'c' }])
 * @param environment - The environment to add to the Cache-Tag header (e.g. prod-eu)
 * @returns void
 */
export function useAddProductsCacheTagHeader({
  products,
}: {
  products: MaybeRefOrGetter<Array<{ id: string }> | undefined>
}) {
  if (import.meta.server || isTestEnv()) {
    const { environment } = useRuntimeConfig().public.httpcache

    useWatchAndAddCacheTags(
      () => toValue(products),
      (newProducts) =>
        newProducts?.map((product) => `${environment}-${product.id}`),
    )
  }
}

/**
 * This composable adds an already-formatted 'Cache-Tag' header value to the response.
 * Unlike `useAddProductsCacheTagHeader`, it does not build tags from product ids: it
 * expects the raw, fully-formatted header string returned by an upstream API
 * (e.g. the pickers endpoint returns "prod-eu-a,prod-eu-b").
 *
 * It reacts to the source updating asynchronously (e.g. after a `useAsyncData` resolves)
 * and applies the tag synchronously so it lands before the SSR response is sent.
 *
 * It's a no-op on the client or if the header value is empty.
 *
 * @param header - The raw Cache-Tag header value (or a ref/getter resolving to it)
 * @returns void
 */
export function useAddRawCacheTagHeader(
  header: MaybeRefOrGetter<string | undefined>,
) {
  // NOTE: do not gate on `toValue(header)` here. The source is typically a
  // getter/ref that resolves asynchronously (e.g. after a `useAsyncData`),
  // so it's empty at call time. Reading it in the guard would short-circuit
  // before the watcher is ever set up, and the tag would never land.
  // Empty/undefined values are a no-op downstream (see `toTags` below).
  if (import.meta.server || isTestEnv()) {
    useWatchAndAddCacheTags(
      () => toValue(header),
      (value) => (value ? [value] : undefined),
    )
  }
}
